
import React from 'react';
import { useTour } from '@reactour/tour';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Diamond, Sparkles } from 'lucide-react';

interface WalkthroughContentProps {
  currentStep: number;
  totalSteps: number;
}

export const WalkthroughContent: React.FC<WalkthroughContentProps> = ({ currentStep, totalSteps }) => {
  const { setCurrentStep, setIsOpen } = useTour();

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setIsOpen(false);
      localStorage.setItem('walkthrough-completed', 'true');
    }
  };

  const handleSkip = () => {
    setIsOpen(false);
    localStorage.setItem('walkthrough-completed', 'true');
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 0:
        return {
          title: "Bem-vinda ao X do Infinito! ✨",
          content: "Este é seu portal completo para vendas por consignado. Vamos fazer um tour rápido pelas principais funcionalidades!",
          icon: <Sparkles className="w-6 h-6 text-gold" />
        };
      case 1:
        return {
          title: "Suas Vendas e Estatísticas 📊",
          content: "Aqui você acompanha o resumo das suas vendas, diamantes conquistados e sua posição no ranking.",
          icon: <Diamond className="w-6 h-6 text-purple" />
        };
      case 2:
        return {
          title: "Ações Rápidas ⚡",
          content: "Use estes botões para acessar rapidamente as principais funcionalidades: Nova Venda, Minhas Vendas, Benefícios e Ranking.",
          icon: <Diamond className="w-6 h-6 text-primary" />
        };
      case 3:
        return {
          title: "Vendas Recentes 📝",
          content: "Acompanhe suas vendas mais recentes e seus status de aprovação diretamente na tela inicial.",
          icon: <Diamond className="w-6 h-6 text-success" />
        };
      case 4:
        return {
          title: "Programa de Benefícios 💎",
          content: "Veja os próximos benefícios que você pode resgatar com seus diamantes conquistados nas vendas.",
          icon: <Diamond className="w-6 h-6 text-gold" />
        };
      default:
        return {
          title: "Pronto para começar! 🎉",
          content: "Agora você conhece as principais funcionalidades. Comece fazendo sua primeira venda!",
          icon: <Sparkles className="w-6 h-6 text-success" />
        };
    }
  };

  const stepContent = getStepContent();

  return (
    <Card className="w-80 border-primary/20 shadow-lg">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          {stepContent.icon}
          <h3 className="text-lg font-semibold text-foreground">
            {stepContent.title}
          </h3>
        </div>
        
        <p className="text-muted-foreground mb-6 leading-relaxed">
          {stepContent.content}
        </p>

        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>

          <div className="flex gap-2">
            {currentStep < totalSteps - 1 && (
              <Button variant="ghost" size="sm" onClick={handleSkip}>
                Pular
              </Button>
            )}
            <Button size="sm" onClick={handleNext}>
              {currentStep < totalSteps - 1 ? 'Próximo' : 'Finalizar'}
            </Button>
          </div>
        </div>

        <div className="mt-4 text-xs text-muted-foreground text-center">
          {currentStep + 1} de {totalSteps}
        </div>
      </CardContent>
    </Card>
  );
};
